import { useState, useEffect, useCallback } from "react";
import { ClientOverviewRepositoryImpl } from "../../data/repositories/ClientOverviewRepositoryImpl";
import { GetMonthAtGlanceInsightsUseCase } from "../../domain/use-cases/GetMonthAtGlanceInsightsUseCase";
import type { MonthAtGlanceInsightItem } from "../../domain/entities/MonthAtGlanceInsightsEntity";

const repository = new ClientOverviewRepositoryImpl();
const useCase = new GetMonthAtGlanceInsightsUseCase(repository);

export interface MonthAtGlanceInsightsData {
  insights: MonthAtGlanceInsightItem[];
  noInsights: boolean;
  monthLabel: string;
  message: string | null;
  loading: boolean;
  error: string | null;
}

export interface UseMonthAtGlanceInsightsOptions {
  enabled?: boolean;
}

export function useMonthAtGlanceInsights(
  options?: UseMonthAtGlanceInsightsOptions,
): MonthAtGlanceInsightsData {
  const enabled = options?.enabled ?? true;

  const [insights, setInsights] = useState<MonthAtGlanceInsightItem[]>([]);
  const [noInsights, setNoInsights] = useState(false);
  const [monthLabel, setMonthLabel] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchInsights = useCallback(async () => {
    if (!enabled) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const entity = await useCase.execute();
      setInsights(entity.insights);
      setNoInsights(entity.noInsights);
      setMonthLabel(entity.monthLabel);
      setMessage(entity.message);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to load month at glance insights",
      );
      setInsights([]);
      setNoInsights(true);
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    fetchInsights();
  }, [enabled, fetchInsights]);

  return { insights, noInsights, monthLabel, message, loading, error };
}
